/**
 * The life of one reconcile apply (batch 8 T11, #3032): the same contract as the cost confirmation
 * (`useConfirmFlow`), for the selection of stock places that get an unknown-cost layer.
 *
 * The selection is written down with its key before it is sent and is immutable from then on. While it is
 * unresolved nothing else can be applied: the only ways forward are to send the identical selection again
 * under the identical key, or to abandon it knowingly.
 */
import { useCallback, useMemo } from 'react';
import { useReconcileCosting, type ReconcileOutcome } from '@/hooks/useInventoryCosting';
import {
  clearReconcile,
  dropUnusableReconcile,
  loadReconcile,
  newOperationKey,
  saveReconcile,
  type PendingReconcile,
  type PersistenceStatus,
  type ReconcileSelection,
} from './pendingOperation';
import { useKeyedIntent, type FlowPhase, type IntentAdapter } from './useKeyedIntent';

export interface ReconcileFlow {
  readonly phase: FlowPhase;
  readonly pending: PendingReconcile | null;
  readonly persistence: PersistenceStatus | null;
  readonly restored: boolean;
  readonly discardedStored: boolean;
  readonly storageUnreadable: boolean;
  readonly refusal: unknown;
  readonly outcome: ReconcileOutcome | null;
  start(keys: readonly ReconcileSelection[]): Promise<ReconcileOutcome | null>;
  retry(): Promise<ReconcileOutcome | null>;
  discard(): void;
  dismissRefusal(): void;
  dismissOutcome(): void;
  dismissDiscardedStored(): void;
}

export function useReconcileFlow(): ReconcileFlow {
  const mutation = useReconcileCosting();
  const send = mutation.mutateAsync;

  const adapter = useMemo<IntentAdapter<PendingReconcile, ReconcileOutcome>>(
    () => ({
      kind: 'reconcile',
      load: loadReconcile,
      save: saveReconcile,
      clear: clearReconcile,
      dropUnusable: dropUnusableReconcile,
      send: (intent) => send({ key: intent.key, request: { apply: true, keys: intent.keys.map((k) => ({ partId: k.partId, warehouseId: k.warehouseId })) }, scope: intent.scope }),
    }),
    [send],
  );
  const intent = useKeyedIntent(adapter);
  const { begin, retry, discard, dismissRefusal, dismissOutcome, dismissDiscarded } = intent;

  const start = useCallback(
    (keys: readonly ReconcileSelection[]) =>
      begin((scope) => ({
        key: newOperationKey(),
        scope,
        keys: keys.map((k) => ({ partId: k.partId, warehouseId: k.warehouseId })),
        startedAt: new Date().toISOString(),
      })),
    [begin],
  );

  return {
    phase: intent.phase,
    pending: intent.intent,
    persistence: intent.persistence,
    restored: intent.restored,
    discardedStored: intent.discarded,
    storageUnreadable: intent.storageUnreadable,
    refusal: intent.refusal,
    outcome: intent.outcome,
    start,
    retry,
    discard,
    dismissRefusal,
    dismissOutcome,
    dismissDiscardedStored: dismissDiscarded,
  };
}
